import type { FieldType, FormField } from '@re/form-engine';
import { Box, ButtonBase, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FIELD_TYPE } from '../../lib/labels';
import { FieldIcon, FIELD_META } from './fieldMeta';
import { TYPE_GROUPS } from './helpers';
import { tr } from '../../lib/i18n';

/** Visual picker of field types, grouped, with icons and one-line hints. */
export function AddFieldDialog({ open, onClose, onPick, fields }: { open: boolean; onClose: () => void; onPick: (t: FieldType) => void; fields: FormField[] }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {tr('إضافة حقل', 'Add field')}
        <Typography variant="body2" color="text.secondary">{tr('يُضاف بعد الحقل المحدد حالياً', 'Added after the currently selected field')}</Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 12, left: 12 }} aria-label={tr('إغلاق', 'Close')}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {TYPE_GROUPS.map((g) => (
            <Box key={g.label}>
              <Typography variant="overline" color="text.secondary">{g.label}</Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, mt: 0.5 }}>
                {g.types.map((t) => {
                  const disabled = t === 'request_type' && fields.some((f) => f.type === 'request_type');
                  return (
                    <ButtonBase
                      key={t}
                      disabled={disabled}
                      onClick={() => onPick(t)}
                      sx={{
                        p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider', textAlign: 'right', display: 'flex', alignItems: 'flex-start', gap: 1.25, justifyContent: 'flex-start',
                        opacity: disabled ? 0.45 : 1, transition: 'all .15s',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                      }}
                    >
                      <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', flexShrink: 0, opacity: 0.9 }}>
                        <FieldIcon type={t} fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>{FIELD_TYPE[t]}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>{disabled ? tr('موجود بالفعل', 'Already added') : FIELD_META[t].hint}</Typography>
                      </Box>
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
